<template>
  <div
    id="app"
    class="vh-100 h-100 position-relative"
    :class="[skinClasses]"
  >
    <component :is="layout">
      <BreadCrumb />
      <router-view />
    </component>

    <app-tour :steps="stepsHelperTour" />
    <chat-widget v-if="showChatWidget" />

    <!-- Onboarding wizard (obrigatorio) -->
    <onboarding-wizard
      v-if="showOnboarding"
      @completed="onOnboardingDone"
    />
  </div>
</template>

<script>
import { $themeColors, $themeBreakpoints, $themeConfig } from '@themeConfig'
import { provideToast } from 'vue-toastification/composition'
import useAppConfig from '@core/app-config/useAppConfig'
import AppTour from '@core/components/app-tour/AppTour.vue'
import { mapState } from 'vuex'

import store from '@/store'
import ChatWidget from '@/components/ChatWidget.vue'
import OnboardingWizard from '@/components/OnboardingWizard.vue'

// Layouts carregados de forma sincrona para evitar flash sem estilo (FOUC)
import LayoutVertical from '@/layouts/vertical/LayoutVertical.vue'
import LayoutFull from '@/layouts/full/LayoutFull.vue'
import LayoutApp from '@/layouts/app/LayoutApp.vue'

const LayoutHorizontal = () => import('@/layouts/horizontal/LayoutHorizontal.vue')

export default {
  components: {
    ChatWidget,
    OnboardingWizard,
    AppTour,
    // Layouts
    LayoutHorizontal,
    LayoutVertical,
    LayoutFull,
    LayoutApp,
  },
  data() {
    return {
      skinClasses: 'skin-light',
      showChat: false,
      onboardingCompleted: false,
    }
  },
  computed: {
    ...mapState({
      usuarioLogado: state => state.User.usuarioLogado,
    }),
    layout() {
      if (this.$route.meta.layout === 'full') return 'layout-full'
      // Keep login/public auth flows on the legacy/public shell.
      const isPublicAuthRoute = this.$route.meta.resource === 'Auth' && this.$route.meta.requiresAuth === false
      if (isPublicAuthRoute) return `layout-${this.contentLayoutType}`
      // Every other non-full route is part of the product area and should
      // render inside the dashboard shell (sidebar + topbar + dark tokens).
      return 'layout-app'
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type
    },
    showOnboarding() {
      if (this.onboardingCompleted) return false
      return this.checkNeedsOnboarding()
    },
    showChatWidget() {
      if (!this.$store.state.User.usuarioLogado) return false
      if (this.$route.meta.layout === 'full') return false
      // Evita que scripts externos do widget quebrem telas de Treinamentos.
      return !['app-treinamentos', 'academy-student'].includes(this.$route.name)
    },
    stepsHelperTour: {
      get() {
        return this.$store.state.appHelperTour.stepsHelper
      },
      set(val) {
        this.$store.commit('appHelperTour/SET_STEPS_HELPER', val)
      },
    },
    isTypeHelperTour() {
      const typeHelperTour = this.$store.getters['appHelperTour/typeHelperTour']
      if (typeHelperTour === null) {
        this.$store.dispatch('appHelperTour/restartTours')
        return typeHelperTour
      }
      return typeHelperTour
    },
  },
  watch: {
    isTypeHelperTour(val) {
      if (val === null) {
        this.$store.dispatch('appHelperTour/restartTours')
      }
    },
    $route(to) {
      if (this.isPublicGTMRoute(to.name)) {
        this.injectGTM()
      } else {
        this.removeGTM()
      }
    },
  },
  created() {
    this.injectGTM()
  },
  // ! We can move this computed: layout & contentLayoutType once we get to use Vue 3
  // Currently, router.currentRoute is not reactive and doesn't trigger any change

  beforeCreate() {
    // Set colors in theme
    const colors = ['primary', 'secondary', 'success', 'info', 'warning', 'danger', 'light', 'dark']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = window.getComputedStyle(document.documentElement).getPropertyValue(`--${colors[i]}`).trim()
    }

    // Set Theme Breakpoints
    const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(window.getComputedStyle(document.documentElement).getPropertyValue(`--breakpoint-${breakpoints[i]}`).trim().slice(0, -2))
    }

    // Set RTL
    const { isRTL } = $themeConfig.layout
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr')
  },
  methods: {
    checkNeedsOnboarding() {
      try {
        const ud = JSON.parse(localStorage.getItem('userData') || 'null')
        if (!ud || !ud.id) return false
        return ud.user_onboarded !== 1 && ud.user_onboarded !== '1'
      } catch { return false }
    },
    onOnboardingDone() {
      this.onboardingCompleted = true
    },

    /** Retorna true se há sessão autenticada ativa */
    isAuthenticated() {
      try {
        const userData = JSON.parse(localStorage.getItem('userData') || 'null')
        const token = localStorage.getItem('accessToken') || localStorage.getItem('token')
        return !!(userData && userData.id && token)
      } catch { return false }
    },

    /** Rotas públicas onde o GTM pode estar ativo (nunca em sessão autenticada) */
    isPublicGTMRoute(routeName) {
      if (this.isAuthenticated()) return false
      return ['home', 'auth-login', 'auth-login-brc', 'cadastro-trial', 'cadastro'].includes(routeName)
    },

    /** Remove todos os elementos do GTM do DOM e limpa o dataLayer */
    removeGTM() {
      document.querySelectorAll('script[data-gtm]').forEach(el => el.remove())
      document.querySelectorAll('noscript[data-gtm-noscript]').forEach(el => el.remove())
      // Remove scripts do gtm.js já carregados pelo container
      document.querySelectorAll('script[src*="googletagmanager.com"]').forEach(el => el.remove())
      // Esvazia o dataLayer para não vazar eventos para a área logada
      if (window.dataLayer) window.dataLayer = []
    },

    /** Busca o GTM ID na API e injeta o script no <head> apenas em páginas públicas */
    async injectGTM() {
      try {
        if (!this.isPublicGTMRoute(this.$route && this.$route.name)) return

        const base = process.env.VUE_APP_HOST_API_BACKEND || '/api/dkbackend'
        const resp = await fetch(`${base}/tag-manager/snippet`)
        const data = await resp.json()
        if (!data.gtm_id) return

        // Evita injetar duas vezes
        if (document.querySelector('script[data-gtm]')) return

        const gtmId = data.gtm_id

        // Script principal no <head>
        const script = document.createElement('script')
        script.setAttribute('data-gtm', gtmId)
        script.textContent = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${gtmId}');`
        document.head.appendChild(script)

        // noscript iframe no <body>
        const noscript = document.createElement('noscript')
        noscript.setAttribute('data-gtm-noscript', gtmId)
        noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`
        document.body.insertBefore(noscript, document.body.firstChild)
      } catch {
        // Silencia erros — GTM é opcional
      }
    },
  },
  setup() {
    const { skin, skinClasses } = useAppConfig()

    // If skin is dark when initialized => Add class to body
    if (skin.value === 'dark') document.body.classList.add('dark-layout')

    // Provide toast for Composition API usage
    // This for those apps/components which uses composition API
    // Demos will still use Options API for ease
    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      timeout: 3000,
      transition: 'Vue-Toastification__fade',
    })

    // Set Window Width in store
    store.commit('app/UPDATE_WINDOW_WIDTH', window.innerWidth)
    window.addEventListener('resize', () => {
      store.commit('app/UPDATE_WINDOW_WIDTH', window.innerWidth)
    })

    return {
      skinClasses,
    }
  },

}
</script>
